import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Box, Button, TextField, Typography, IconButton, Paper, Dialog, DialogTitle, DialogContent, Grid } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import TerminalIcon from '@mui/icons-material/Terminal';
import MemoryIcon from '@mui/icons-material/Memory';
import InputIcon from '@mui/icons-material/Input';
import { useTestProcedure } from "../../context/TestProcedureContext.tsx";
import { useElectronicsConfiguration } from "../../context/ElectronicsConfigurationContext.tsx";
import { TestStep, TestStepType } from "../../models/testStep.ts";
import { v4 as uuidv4 } from "uuid";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { TestProcedure } from "../../models/testProcedure.ts";
import EditWaitForm from "./EditWaitForm.tsx";
import { Wait } from "../../models/testSteps/wait.ts";
import { SwitchRelais } from "../../models/testSteps/switchRelais.ts";
import EditSwitchRelaisForm from "./EditSwitchRelaisForm.tsx";
import EditMeasureVoltageForm from "./EditMeasureVoltageForm.tsx";
import EditMeasureCurrentForm from "./EditMeasureCurrentForm.tsx";
import EditExternalCommandForm from "./EditExternalCommandForm.tsx";
import EditStLinkForm from "./EditStLinkForm.tsx";
import EditUserInputForm from "./EditUserInputForm.tsx";
import { MeasureVoltage } from "../../models/testSteps/measureVoltage.ts";
import { MeasureCurrent } from "../../models/testSteps/measureCurrent.ts";
import { ExternalCommand } from "../../models/testSteps/externalCommand.ts";
import { StLink } from "../../models/testSteps/stLink.ts";
import { UserInput } from "../../models/testSteps/userInput.ts";
import FieldWithTrailingIcon from "../../components/FieldWithTrailingIcon.tsx"
import InfoDialog from "../../components/InfoDialog.tsx"; 
import { isStepValid } from "../../utils/testProcedureValidation.ts";
import { useTheme } from "@mui/material";
import { ImageBox } from "../../components/ImageBox.tsx";
import { ManualBoolInfoDialog } from "./ManualBoolInfo.tsx";
import { ManualChoiceInfoDialog } from "./ManualChoiceInfo.tsx";
import PageLayout from '../../components/PageLayout.tsx';

const TestProcedureDetailsPage: React.FC = () => {
  const [procedure, setProcedure] = useState<TestProcedure>({id:"", name:"", serial_number_format: "", steps:[]});
  const [openDialog, setOpenDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState<Wait | SwitchRelais | MeasureVoltage | MeasureCurrent | ExternalCommand | StLink | UserInput | null>(null);
  const [isDialogValid, setIsDialogValid] = useState(false);
  const [serialNumberInfoOpen, setSerialNumberInfoOpen] = useState(false);
  const [manualBoolInfoOpen, setManualBoolInfoOpen] = useState(false);
  const [manualChoiceInfoOpen, setManualChoiceInfoOpen] = useState(false);
  const [isSerialNumberFormatValid, setIsSerialNumberFormatValid] = useState(true);
  const { id } = useParams();
  const { procedures, updateProcedure } = useTestProcedure();
  const { config: electronicsConfig } = useElectronicsConfiguration();
  const theme = useTheme();

  const reindexSteps = (steps: TestStep[]): TestStep[] =>
    steps.map((step, idx) => ({ ...step, index: idx }));

  const setProcedureAndSync = (procedure: TestProcedure) => {
    if (id) {
      const selected = procedures.find(p => p.id === id);
      if (selected) {
        setProcedure(procedure);
        updateProcedure(id, procedure);
      }
    }
  }

  const isSerialFormatValid = (format: string): boolean => {
    if (format === "") return true;
    try {
      new RegExp(format);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (id) {
      const selected = procedures.find(p => p.id === id);
      if (selected) {
        setProcedure(selected);
      }
    }
  }, [procedures, id]);

  useEffect(() => {
    setIsSerialNumberFormatValid(
      isSerialFormatValid(procedure.serial_number_format ?? "")
    );
  }, [procedure.serial_number_format]);

  const handleNameChange = (name: string) => {
    const updated = procedure;
    if (updated) {
        updated.name = name;
        setProcedureAndSync(updated);
    }
  };

  const handleSerialNumberFormatChange = (format: string) => {
    const updated = { ...procedure, serial_number_format: format };
    setProcedureAndSync(updated);
    setIsSerialNumberFormatValid(isSerialFormatValid(format));
  };
  
  const handleSaveStep = () => {
    if (!currentStep || !procedure) return;
    const existingIndex = procedure.steps.findIndex(s => s.id === currentStep.id);
    const updatedSteps = [...procedure.steps];
    if (existingIndex >= 0) {
      updatedSteps[existingIndex] = currentStep;
    } else {
      updatedSteps.push(currentStep);
    }
    updatedSteps.forEach((s, i) => s.index = i);
    setProcedureAndSync({ ...procedure, steps: updatedSteps });
    setOpenDialog(false);
    setCurrentStep(null);
    setIsDialogValid(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentStep(null);
    setIsDialogValid(false);
  };

  const handleOpenDialog = (index: number) => {
    const step = procedure.steps.find(s => s.index === index);
    if (step) {
      if (step.type === "Wait") {
        setCurrentStep(step as Wait);
      } else if (step.type === "SwitchRelais") {
        setCurrentStep(step as SwitchRelais);
      } else if (step.type === "MeasureVoltage") {
        setCurrentStep(step as MeasureVoltage);
      } else if (step.type === "MeasureCurrent") {
        setCurrentStep(step as MeasureCurrent);
      } else if (step.type === "ExternalCommand") {
        setCurrentStep(step as ExternalCommand);
      } else if (step.type === "StLink") {
        setCurrentStep(step as StLink);
      } else if (step.type === "ManualBool" || step.type === "ManualChoice") {
        setCurrentStep(step as UserInput);
      }
      setOpenDialog(true);
    }
  };

  const handleDeleteStep = (index: number) => {
    if (procedure) {
        const updated = procedure;
        updated.steps = reindexSteps(updated.steps.filter((_, i) => i !== index));
        setProcedureAndSync(updated);
    }
  };

  const handleAddStep = (type: TestStepType) =>  {
    if (procedure) {
        const newStep: TestStep = {
            id: uuidv4(),
            index: procedure.steps.length,
            name: "",
            type: type,
        };
      if (newStep.type === "Wait") {
        newStep.name = `Warten (${procedure.steps.length + 1})`;
        (newStep as Wait).wait_ms = 1000;
      } else if (newStep.type === "SwitchRelais") {
        newStep.name = `Schalte Relais (${procedure.steps.length + 1})`;
        (newStep as SwitchRelais).relais_id = "";
        (newStep as SwitchRelais).status = false;
        (newStep as SwitchRelais).switch_name = "";
      } else if (newStep.type === "MeasureVoltage") {
        newStep.name = `Spannung Messen (${procedure.steps.length + 1})`;
        (newStep as MeasureVoltage).enable_max = false;
        (newStep as MeasureVoltage).enable_min = false;
        (newStep as MeasureVoltage).max = 0;
        (newStep as MeasureVoltage).min = 0;
        (newStep as MeasureVoltage).voltage_id = "";
        (newStep as MeasureVoltage).voltage_name = "";
      } else if (newStep.type === "MeasureCurrent") {
        newStep.name = `Strom Messen (${procedure.steps.length + 1})`;
        (newStep as MeasureCurrent).enable_max = false;
        (newStep as MeasureCurrent).enable_min = false;
        (newStep as MeasureCurrent).max = 0;
        (newStep as MeasureCurrent).min = 0;
        (newStep as MeasureCurrent).current_id = "";
        (newStep as MeasureCurrent).current_name = "";
      } else if (newStep.type === "ExternalCommand") {
        newStep.name = `Externes Kommando (${procedure.steps.length + 1})`;
        (newStep as ExternalCommand).args = [];
        (newStep as ExternalCommand).exec = "";
        (newStep as ExternalCommand).result = "";
        (newStep as ExternalCommand).timeout = 1000;
      } else if (newStep.type === "StLink") {
        newStep.name = `Firmware via STLink flashen (${procedure.steps.length + 1})`;
        (newStep as StLink).firmware_data = "";
        (newStep as StLink).firmware_path = "";
        (newStep as StLink).st_link_index = 0;
      } else if (newStep.type === "ManualBool" || newStep.type === "ManualChoice") {
        newStep.name = `Nutzereingabe (${procedure.steps.length + 1})`;
        (newStep as UserInput).image_data = "";
        (newStep as UserInput).image_path = "";
        (newStep as UserInput).text = "";
      }
        const updated = procedure;
        updated.steps = [...procedure.steps, newStep];
        setProcedureAndSync(updated);
        handleOpenDialog(newStep.index);
    }
  }

  const handleCopyStep = (index: number) => {
    const stepToCopy = procedure.steps[index];
    const newStep = {
      ...stepToCopy,
      id: uuidv4(),
      name: `${stepToCopy.name} Kopie`,
      index: stepToCopy.index + 1,
    };

    const updatedSteps = [...procedure.steps];
    updatedSteps.splice(index + 1, 0, newStep);
    const reindexed = reindexSteps(updatedSteps);
    setProcedureAndSync({ ...procedure, steps: reindexed });
  };

  const handleMoveStepUp = (index: number) => {
    if (index === 0) return;
    const steps = [...procedure.steps];
    [steps[index - 1], steps[index]] = [steps[index], steps[index - 1]];
    setProcedureAndSync({ ...procedure, steps: reindexSteps(steps) });
  };

  const handleMoveStepDown = (index: number) => {
    if (index === procedure.steps.length - 1) return;
    const steps = [...procedure.steps];
    [steps[index], steps[index + 1]] = [steps[index + 1], steps[index]];
    setProcedureAndSync({ ...procedure, steps: reindexSteps(steps) });
  };

  if (!id) {
      return <div>Keine gültige ID</div>;
  }
  return (
    <PageLayout
      title="Prüfablauf bearbeiten"
      backText="Zur Übersicht"
      backTarget="/testprocedure"
      nextText=""
      nextTarget=""
    >
      <Box mt={2}>
        <TextField
          label="Name"
          value={procedure?.name}
          fullWidth
          onChange={(e) => handleNameChange(e.target.value)}
        />
      </Box>
      <Box mt={2}>
        <FieldWithTrailingIcon
          icon={<InfoOutlinedIcon fontSize="small" />}
          onIconClick={() => setSerialNumberInfoOpen(true)}
        >
          <TextField
            label="Format Seriennummer (Regex)"
            value={procedure?.serial_number_format}
            fullWidth
            error={!isSerialNumberFormatValid}
            onChange={(e) => handleSerialNumberFormatChange(e.target.value)}
          />
        </FieldWithTrailingIcon>
      </Box>
      {procedure?.steps.length > 0 && (
        <Box mt={4}>
          <Typography variant="h6">Testschritte</Typography>
          {procedure?.steps.map((step, index) => {
            const valid = isStepValid(step, electronicsConfig);
            return (
              <Paper 
                key={step.id} 
                sx={{ 
                  p: 2, 
                  mt: 2, 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  border: valid ? '1px solid transparent' : '1px solid red',
                  backgroundColor: valid ? "inherit" : theme.palette.error.main + "22",
              }}>
                <Box ml={2} mr={2}>
                  <Typography variant="subtitle1">{step.index+1}</Typography>
                </Box>
                <Box>
                  <IconButton size="small" onClick={() => handleMoveStepUp(index)} disabled={index === 0}>
                    <ArrowUpwardIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleMoveStepDown(index)} disabled={index === procedure.steps.length - 1}>
                    <ArrowDownwardIcon />
                  </IconButton>
                </Box>
                <Box sx={{ flexGrow: 1, ml:2, mr:2}}>
                  <Typography variant="subtitle1">{step.name} ({step.type})</Typography>
                  {step?.type === "Wait" && (
                    <>
                      <Typography variant="body2" color="text.secondary">Zeit: {(step as Wait).wait_ms} ms</Typography>
                    </>
                  )}
                  {step?.type === "SwitchRelais" && (
                    <>
                      <Typography variant="body2" color="text.secondary">Relais: {(step as SwitchRelais).switch_name}</Typography>
                      <Typography variant="body2" color="text.secondary">Status: {(step as SwitchRelais).status ? "geschlossen" : "offen"}</Typography>
                    </>
                  )}
                  {step?.type === "MeasureVoltage" && (
                    <>
                      <Typography variant="body2" color="text.secondary">Spannung: {(step as MeasureVoltage).voltage_name}</Typography>
                      {(step as MeasureVoltage).enable_min && (
                        <Typography variant="body2" color="text.secondary">Min.: {(step as MeasureVoltage).min}V</Typography>
                      )}
                      {(step as MeasureVoltage).enable_max && (
                        <Typography variant="body2" color="text.secondary">Max.: {(step as MeasureVoltage).max}V</Typography>
                      )}
                    </>
                  )}
                  {step?.type === "MeasureCurrent" && (
                    <>
                      <Typography variant="body2" color="text.secondary">Strom: {(step as MeasureCurrent).current_name}</Typography>
                      {(step as MeasureCurrent).enable_min && (
                        <Typography variant="body2" color="text.secondary">Min.: {(step as MeasureCurrent).min}A</Typography>
                      )}
                      {(step as MeasureCurrent).enable_max && (
                        <Typography variant="body2" color="text.secondary">Max.: {(step as MeasureCurrent).max}A</Typography>
                      )}
                    </>
                  )}
                  {step?.type === "ExternalCommand" && (
                    <>
                      <Typography variant="body2" color="text.secondary">Exec: {(step as ExternalCommand).exec}</Typography>
                      <Typography variant="body2" color="text.secondary">Args: {(step as ExternalCommand).args}</Typography>
                      <Typography variant="body2" color="text.secondary">Result: {(step as ExternalCommand).result}</Typography>
                      <Typography variant="body2" color="text.secondary">Timeout: {(step as ExternalCommand).timeout} ms</Typography>
                    </>
                  )}
                  {step?.type === "StLink" && (
                    <>
                      <Typography variant="body2" color="text.secondary">Index: {(step as StLink).st_link_index}</Typography>
                      <Typography variant="body2" color="text.secondary">Firmware: {(step as StLink).firmware_path}</Typography>
                    </>
                  )}
                  {(step?.type === "ManualBool" || step?.type === "ManualChoice")  && (
                    <>
                      <Typography variant="body2" color="text.secondary">Frage: {(step as UserInput).text}</Typography>
                      {(step as UserInput).image_path?.length > 0 && (
                        <>
                          <Typography variant="body2" color="text.secondary">Bild: {(step as UserInput).image_path}</Typography>
                          <ImageBox
                              src={`data:image/*;base64,${(step as UserInput).image_data}`}
                              alt="Vorschau"
                              width="auto"
                              height={100}
                          />
                        </>
                      )}
                    </>
                  )}

                </Box>
                <Box>
                  <IconButton size="small" color="primary" onClick={() => handleOpenDialog(index)}><EditIcon /></IconButton>
                  <IconButton size="small" color="inherit" onClick={() => handleCopyStep(index)}><ContentCopyIcon /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteStep(index)}><DeleteIcon /></IconButton>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
      <Box mt={2} />
      <Typography variant="h6">Testschritt hinzufügen</Typography>
      <Box mt={2} />
      <Grid container spacing={2}>
        <Grid size={12}>
          <Button variant="contained" startIcon={<AccessTimeIcon />} onClick={() => handleAddStep("Wait")}>
            Warten
          </Button>
        </Grid>
        {electronicsConfig.signal_mapping.relais.length > 0 && (
          <Grid size={12}>
            <Button variant="contained" startIcon={<ToggleOnIcon />} onClick={() => handleAddStep("SwitchRelais")}>
              Schalte Relais
            </Button>
          </Grid>
        )}
        {electronicsConfig.signal_mapping.voltage.length > 0 && (
          <Grid size={12}>
            <Button variant="contained" startIcon={<FlashOnIcon />} onClick={() => handleAddStep("MeasureVoltage")}>
              Spannung Messen
            </Button>
          </Grid>
        )}
        {electronicsConfig.signal_mapping.current.length > 0 && (
          <Grid size={12}>
            <Button variant="contained" startIcon={<BatteryChargingFullIcon />} onClick={() => handleAddStep("MeasureCurrent")}>
              Strom Messen
            </Button>
          </Grid>
        )}
        <Grid size={12}>
          <Button variant="contained" startIcon={<TerminalIcon />} onClick={() => handleAddStep("ExternalCommand")}>
            Externes Kommando
          </Button>
        </Grid>
        {(electronicsConfig.flasher.stlink1.enable || electronicsConfig.flasher.stlink2.enable) && (
          <Grid size={12}>
            <Button variant="contained" startIcon={<MemoryIcon />} onClick={() => handleAddStep("StLink")}>
              ST-Link Flashen
            </Button>
          </Grid>
        )}
        <Grid size={12}>
          <Button variant="contained" startIcon={<InputIcon />} onClick={() => handleAddStep("ManualBool")}>
            Nutzereingabe
          </Button>
          <IconButton
            size="small"
            onClick={() => setManualBoolInfoOpen(true)}
            aria-label="Info"
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Grid>
        <Grid size={12}>
          <Button variant="contained" startIcon={<InputIcon />} onClick={() => handleAddStep("ManualChoice")}>
            Nutzereingabe mit Bestätigung
          </Button>
          <IconButton
            size="small"
            onClick={() => setManualChoiceInfoOpen(true)}
            aria-label="Info"
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        {currentStep?.type === "Wait" && (
          <DialogTitle>Warten</DialogTitle>
        )}
        {currentStep?.type === "SwitchRelais" && (
          <DialogTitle>Schalte Relais</DialogTitle>
        )}
        {currentStep?.type === "MeasureVoltage" && (
          <DialogTitle>Spannung Messen</DialogTitle>
        )}
        {currentStep?.type === "MeasureCurrent" && (
          <DialogTitle>Strom Messen</DialogTitle>
        )}
        {currentStep?.type === "ExternalCommand" && (
          <DialogTitle>Externes Kommando</DialogTitle>
        )}
        {currentStep?.type === "ManualBool" && (
          <DialogTitle>Nutzereingabe</DialogTitle>
        )}
        {currentStep?.type === "ManualChoice" && (
          <DialogTitle>Nutzereingabe mit Bestätigung</DialogTitle>
        )}
        <DialogContent>
          {currentStep?.type === "Wait" && (
            <EditWaitForm
              data={currentStep as Wait}
              onChange={setCurrentStep}
              setIsValid={setIsDialogValid}
            />
          )}
          {currentStep?.type === "SwitchRelais" && (
            <EditSwitchRelaisForm
              data={currentStep as SwitchRelais}
              onChange={setCurrentStep}
              setIsValid={setIsDialogValid}
              relaisOptions={electronicsConfig.signal_mapping.relais}
            />
          )}
          {currentStep?.type === "MeasureVoltage" && (
            <EditMeasureVoltageForm
              data={currentStep as MeasureVoltage}
              onChange={setCurrentStep}
              setIsValid={setIsDialogValid}
              voltageOptions={electronicsConfig.signal_mapping.voltage}
            />
          )}
          {currentStep?.type === "MeasureCurrent" && (
            <EditMeasureCurrentForm
              data={currentStep as MeasureCurrent}
              onChange={setCurrentStep}
              setIsValid={setIsDialogValid}
              currentOptions={electronicsConfig.signal_mapping.current}
            />
          )}
          {currentStep?.type === "ExternalCommand" && (
            <EditExternalCommandForm
              data={currentStep as ExternalCommand}
              onChange={setCurrentStep}
              setIsValid={setIsDialogValid}
            />
          )}
          {currentStep?.type === "StLink" && (
            <EditStLinkForm
              data={currentStep as StLink}
              onChange={setCurrentStep}
              setIsValid={setIsDialogValid}
              numberOfStLinks={
                (electronicsConfig.flasher.stlink1.enable ? 1 : 0) +
                (electronicsConfig.flasher.stlink2.enable ? 1 : 0)
              }
            />
          )}
          {(currentStep?.type === "ManualBool" || currentStep?.type === "ManualChoice") && (
            <EditUserInputForm
              data={currentStep as UserInput}
              onChange={setCurrentStep}
              setIsValid={setIsDialogValid}
            />
          )}
          <Box mt={2} display="flex" gap={2}>
            <Button variant="contained" onClick={handleSaveStep} disabled={!isDialogValid}>Speichern</Button>
            <Button variant="outlined" onClick={handleCloseDialog}>Abbrechen</Button>
          </Box>
        </DialogContent>
      </Dialog>
      <InfoDialog
        open={serialNumberInfoOpen}
        onClose={() => setSerialNumberInfoOpen(false)}
        title="Seriennummernüberprüfung"
        text={`Anhand eines Regexausdrucks kann die Seriennumerneingabe überprüft werden, sodass nur gewünsche Seriennummern geprüft werden können. 
          Wenn der Regexausdruck leer ist, wird die Seriennummer nicht geprüft.
          Beispiele:
          1. "qde-\\d+": Die Seriennummer beginnt mit der Textfolge "qde-" und darauf hin folgt eine beliebig lange Zahlenfolge
          2. "qde-\\d{4,6}: Die Seriennummer beginnt mit der Textfolge "qde-" und daruf hin folgt eine Zahl mit min. 4 und max. 6 Stellen`}
      />
      <ManualBoolInfoDialog
        open={manualBoolInfoOpen}
        onClose={() => setManualBoolInfoOpen(false)}
      />
      <ManualChoiceInfoDialog
        open={manualChoiceInfoOpen}
        onClose={() => setManualChoiceInfoOpen(false)}
      />
    </PageLayout>
  );
};

export default TestProcedureDetailsPage;
